"use client";

import { BookOpen, Swords, GitMerge, Sparkles, Eye, Brain, Layers, Globe, Check } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { capabilityOverview as data } from "@/lib/data/capabilities-narrative-engineering";

const frameworkIcons = [BookOpen, Swords, GitMerge, Sparkles, Eye];
const frameworkAccents = [
  "var(--color-crimson)",
  "var(--color-amber)",
  "var(--color-cyan)",
  "var(--color-purple)",
  "var(--color-emerald)",
] as const;

export function CapabilityOverview() {
  return (
    <Section id="capability-overview" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 right-1/3 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-cyan)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12">
            <div className="eyebrow-tiny text-[var(--color-crimson)] mb-4">Story Framework Library</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.frameworks.map((f, i) => {
                const Icon = frameworkIcons[i % frameworkIcons.length]!;
                const accent = frameworkAccents[i % frameworkAccents.length]!;
                return (
                  <div
                    key={f.name}
                    className="glass rounded-2xl p-5 h-full"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                    }}
                  >
                    <span
                      className="grid place-items-center h-10 w-10 rounded-lg mb-3"
                      style={{
                        background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                      }}
                    >
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <h3 className="text-base">{f.name}</h3>
                    <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          <Reveal delay={200}>
            <div
              className="glass rounded-2xl p-6 h-full"
              style={{
                boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-purple) 12%, transparent)",
              }}
            >
              <div className="eyebrow-tiny text-[var(--color-purple)] mb-4">Cognitive Bias Calibration</div>
              <div className="space-y-4">
                {data.biasLevels.map((b, i) => (
                  <div key={b.title} className="flex items-start gap-3">
                    <span
                      className="grid place-items-center h-7 w-7 rounded-full text-[10px] font-bold shrink-0"
                      style={{
                        background: "color-mix(in oklch, var(--color-purple) 18%, transparent)",
                        color: "var(--color-purple)",
                      }}
                    >
                      {i + 1}
                    </span>
                    <div>
                      <div className="font-semibold text-sm">{b.title}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-relaxed">{b.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div
              className="glass rounded-2xl p-6 h-full"
              style={{
                boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-emerald) 12%, transparent)",
              }}
            >
              <div className="eyebrow-tiny text-[var(--color-emerald)] mb-4">Multi-Variant Dimensions</div>
              <ul className="space-y-3">
                {data.variantDimensions.map((v) => (
                  <li key={v.label} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-[var(--color-emerald)] mt-0.5 shrink-0" />
                    <div>
                      <span className="font-semibold">{v.label}:</span>{" "}
                      <span className="text-[rgb(var(--fg-muted))] leading-relaxed">{v.description}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={360}>
          <div
            className="mt-10 glass rounded-2xl p-7"
            style={{
              boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-cyan) 12%, transparent)",
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-4 w-4 text-[var(--color-cyan)]" />
              <div className="eyebrow-tiny text-[var(--color-cyan)]">Cultural Localization Framework</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.localization.map((l) => (
                <div key={l} className="flex items-start gap-2 text-sm">
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-[var(--color-cyan)] mt-2 shrink-0"
                  />
                  <span className="text-[rgb(var(--fg-muted))] leading-relaxed">{l}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
